export interface RegionOption {
  label: string
  value: string
  children?: RegionOption[]
}

export interface RegionNode {
  code: string
  name: string
  children?: RegionNode[]
  pinyin?: string
}

let regionTree: RegionNode[] | null = null
const REGION_DATA_URL = '/regions/gb2260-2024.min.json'
const REGION_DATA_CACHE_KEY = 'REGION_DATA_V1'
const REGION_DATA_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7

export async function ensureRegionData(): Promise<void> {
  if (regionTree)
    return
  try {
    const cached = localStorage.getItem(REGION_DATA_CACHE_KEY)
    const cachedAt = localStorage.getItem(`${REGION_DATA_CACHE_KEY}:at`)
    if (cached && cachedAt && Date.now() - Number(cachedAt) < REGION_DATA_CACHE_TTL_MS) {
      regionTree = JSON.parse(cached)
      return
    }
    const res = await fetch(REGION_DATA_URL)
    if (!res.ok)
      throw new Error('region data fetch failed')
    const data = await res.json()
    regionTree = data as RegionNode[]
    localStorage.setItem(REGION_DATA_CACHE_KEY, JSON.stringify(regionTree))
    localStorage.setItem(`${REGION_DATA_CACHE_KEY}:at`, String(Date.now()))
  }
  catch {
    regionTree = null
  }
}

export const PROVINCES: RegionOption[] = [
  {
    label: '北京市',
    value: 'beijing',
    children: [{ label: '北京市', value: 'beijing-city' }],
  },
  {
    label: '上海市',
    value: 'shanghai',
    children: [{ label: '上海市', value: 'shanghai-city' }],
  },
  {
    label: '广东省',
    value: 'guangdong',
    children: [
      { label: '广州市', value: 'guangzhou' },
      { label: '深圳市', value: 'shenzhen' },
      { label: '东莞市', value: 'dongguan' },
      { label: '佛山市', value: 'foshan' },
    ],
  },
  {
    label: '浙江省',
    value: 'zhejiang',
    children: [
      { label: '杭州市', value: 'hangzhou' },
      { label: '宁波市', value: 'ningbo' },
      { label: '温州市', value: 'wenzhou' },
    ],
  },
  {
    label: '江苏省',
    value: 'jiangsu',
    children: [
      { label: '南京市', value: 'nanjing' },
      { label: '苏州市', value: 'suzhou' },
      { label: '无锡市', value: 'wuxi' },
    ],
  },
  {
    label: '四川省',
    value: 'sichuan',
    children: [
      { label: '成都市', value: 'chengdu' },
      { label: '绵阳市', value: 'mianyang' },
    ],
  },
  {
    label: '湖北省',
    value: 'hubei',
    children: [
      { label: '武汉市', value: 'wuhan' },
    ],
  },
  {
    label: '陕西省',
    value: 'shaanxi',
    children: [
      { label: '西安市', value: 'xian' },
    ],
  },
]

export function getProvinces(): RegionOption[] {
  if (regionTree) {
    return regionTree.map(p => ({ label: p.name, value: p.code }))
  }
  return PROVINCES.map(({ label, value }) => ({ label, value }))
}

export function getCities(provinceValue: string): RegionOption[] {
  if (regionTree) {
    const province = regionTree.find(p => p.code === provinceValue)
    return (province?.children || []).map(c => ({ label: c.name, value: c.code }))
  }
  const province = PROVINCES.find(p => p.value === provinceValue)
  return province?.children || []
}

export function getRegionLabel(value: string): string {
  if (regionTree) {
    for (const p of regionTree) {
      if (p.code === value)
        return p.name
      for (const c of p.children || []) {
        if (c.code === value)
          return c.name
        for (const d of c.children || []) {
          if (d.code === value)
            return d.name
        }
      }
    }
  }
  else {
    for (const province of PROVINCES) {
      if (province.value === value)
        return province.label
      if (province.children) {
        const city = province.children.find(c => c.value === value)
        if (city)
          return city.label
      }
    }
  }
  return value
}

export function getDistricts(cityCode: string): RegionOption[] {
  if (!regionTree)
    return []
  for (const p of regionTree) {
    for (const c of p.children || []) {
      if (c.code === cityCode) {
        return (c.children || []).map(d => ({ label: d.name, value: d.code }))
      }
    }
  }
  return []
}

export function getRegionPath(code: string): string {
  if (!regionTree)
    return getRegionLabel(code)
  for (const p of regionTree) {
    if (p.code === code)
      return p.name
    for (const c of p.children || []) {
      if (c.code === code)
        return `${p.name}/${c.name}`
      for (const d of c.children || []) {
        if (d.code === code)
          return `${p.name}/${c.name}/${d.name}`
      }
    }
  }
  return code
}
